// src/routes/Settings.js

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  CssBaseline,
  useMediaQuery,
  useTheme,
  Typography,
  TextField,
  Button,
  Divider,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';

import AppLayout from '../../components/layout/AppLayout';
import { useAuth } from '../../hooks/useAuth';
import { useThemeMode } from '../../contexts/ThemeContext';

// 金額を日本語形式にフォーマットする関数
const formatCurrency = (value) => {
  if (!value || isNaN(value)) return '';
  const num = parseInt(value);
  if (num >= 100000000) {
    const oku = Math.floor(num / 100000000);
    const man = Math.floor((num % 100000000) / 10000);
    if (man === 0) {
      return `${oku}億円`;
    } else {
      return `${oku}億${man}万円`;
    }
  } else if (num >= 10000) {
    const man = Math.floor(num / 10000);
    const remainder = num % 10000;
    if (remainder === 0) {
      return `${man}万円`;
    } else {
      return `${man}万${remainder}円`;
    }
  } else {
    return `${num}円`;
  }
};

// 人数を日本語形式にフォーマットする関数
const formatEmployee = (value) => {
  if (!value || isNaN(value)) return '';
  const num = parseInt(value);
  if (num >= 10000) {
    const man = Math.floor(num / 10000);
    const remainder = num % 10000;
    if (remainder === 0) {
      return `${man}万人`;
    } else {
      return `${man}万${remainder}人`;
    }
  } else {
    return `${num}人`;
  }
};

// 数値のみの入力を許可するハンドラ
const handleNumericInput = (e, setter) => {
  const value = e.target.value.replace(/[^0-9]/g, ''); // 数値以外を除去
  setter(value);
};

function Settings() {
  const { user: currentUser, loading: authLoading } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const BACKEND_URL = process.env.REACT_APP_API_URL;
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  // (サイドバー制御はAppLayoutに統合)

  // ==============================
  // アコーディオンの展開制御
  // ==============================
  const [expanded, setExpanded] = useState(false);
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // ==============================
  // 基本情報 & 顧客情報・検索キー (State)
  // ==============================
  const [companyName, setCompanyName] = useState('');
  const [scale, setScale] = useState('');
  const [location, setLocation] = useState('');
  const [industry, setIndustry] = useState('');
  const [kPublicationName, setKPublicationName] = useState('');
  const [kPublicationNameFuri, setKPublicationNameFuri] = useState('');
  const [kIndustryCode, setKIndustryCode] = useState('');
  const [kStockStatus, setKStockStatus] = useState('');
  const [kHeadOfficePref, setKHeadOfficePref] = useState('');
  const [kBranchOfficePref, setKBranchOfficePref] = useState('');
  const [kEmployeeNumber, setKEmployeeNumber] = useState('');

  // 雇用促進法明示項目
  const [newGradHirePast3, setNewGradHirePast3] = useState('');
  const [newGradLeavePast3, setNewGradLeavePast3] = useState('');
  const [newGradHireGenderPast3, setNewGradHireGenderPast3] = useState('');
  const [averageTenure, setAverageTenure] = useState('');
  const [trainingInfo, setTrainingInfo] = useState('');
  const [selfDevSupport, setSelfDevSupport] = useState('');
  const [mentorSystem, setMentorSystem] = useState('');
  const [careerConsulting, setCareerConsulting] = useState('');
  const [internalCertification, setInternalCertification] = useState('');
  const [avgOvertime, setAvgOvertime] = useState('');
  const [avgPaidLeave, setAvgPaidLeave] = useState('');
  const [childcareTargets, setChildcareTargets] = useState('');
  const [childcareTaken, setChildcareTaken] = useState('');
  const [womenRatio, setWomenRatio] = useState('');

  // 企業基本情報
  const [companyCatch, setCompanyCatch] = useState('');
  const [businessContent, setBusinessContent] = useState('');
  const [hqPostalCode, setHqPostalCode] = useState('');
  const [hqAddress, setHqAddress] = useState('');
  const [hqPhoneNumber, setHqPhoneNumber] = useState('');

  // 会社概要
  const [capital, setCapital] = useState('');
  const [employeesInfo, setEmployeesInfo] = useState('');
  const [sales, setSales] = useState('');

  // パスワード変更用のstate
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // 子アカウント管理用のstate
  const [subAccounts, setSubAccounts] = useState([]);
  const [subAccountLoading, setSubAccountLoading] = useState(false);
  const [subAccountError, setSubAccountError] = useState(null);
  const [openSubAccountModal, setOpenSubAccountModal] = useState(false);
  const [newSubAccount, setNewSubAccount] = useState({
    email: '',
    password: ''
  });
  const [showSubAccountPassword, setShowSubAccountPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // ==============================
  // (A) マウント時に基本情報をGET
  // ==============================
  useEffect(() => {
    const fetchBasicInfo = async () => {
      try {
        // Cookieを使って認証
        const response = await axios.get(`${BACKEND_URL}/basic-info/`, {
          withCredentials: true,
        });
        console.log("GET /basic-info response: ", response.data);

        const data = response.data;
        // 取得したデータを反映
        setCompanyName(data.company_name ?? '');
        setScale(data.scale ?? '');
        setLocation(data.location ?? '');
        setIndustry(data.industry ?? '');
        setKPublicationName(data.k_publication_name ?? '');
        setKPublicationNameFuri(data.k_publication_name_furi ?? '');
        setKIndustryCode(data.k_industry_code ?? '');
        setKStockStatus(data.k_stock_status ?? '');
        setKHeadOfficePref(data.k_head_office_pref ?? '');
        setKBranchOfficePref(data.k_branch_office_pref ?? '');
        setKEmployeeNumber(data.k_employee_number ?? '');
        setNewGradHirePast3(data.new_grad_hire_past_3 ?? '');
        setNewGradLeavePast3(data.new_grad_leave_past_3 ?? '');
        setNewGradHireGenderPast3(data.new_grad_hire_gender_past_3 ?? '');
        setAverageTenure(data.average_tenure ?? '');
        setTrainingInfo(data.training_info ?? '');
        setSelfDevSupport(data.self_dev_support ?? '');
        setMentorSystem(data.mentor_system ?? '');
        setCareerConsulting(data.career_consulting ?? '');
        setInternalCertification(data.internal_certification ?? '');
        setAvgOvertime(data.avg_overtime ?? '');
        setAvgPaidLeave(data.avg_paid_leave ?? '');
        setChildcareTargets(data.childcare_targets ?? '');
        setChildcareTaken(data.childcare_taken ?? '');
        setWomenRatio(data.women_ratio ?? '');
        setCompanyCatch(data.company_catch ?? '');
        setBusinessContent(data.business_content ?? '');
        setHqPostalCode(data.hq_postal_code ?? '');
        setHqAddress(data.hq_address ?? '');
        setHqPhoneNumber(data.hq_phone_number ?? '');
        setCapital(data.capital ?? '');
        setEmployeesInfo(data.employees_info ?? '');
        setSales(data.sales ?? '');
      } catch (error) {
        console.error("Failed to fetch basic info:", error);
      }
    };

    fetchBasicInfo();
  }, [BACKEND_URL]);

  // ==============================
  // (B) バリデーション
  // ==============================
  const validateSettings = () => {
    return true;
  };

  // ==============================
  // (C) 保存 (PUT /basic-info)
  // ==============================
  const handleSave = async () => {
    if (!validateSettings()) return;

    try {
      // Cookieを使って認証
      await axios.put(
        `${BACKEND_URL}/basic-info/`,
        {
          company_name: companyName,
          scale: scale,
          location: location,
          industry: industry,
          k_publication_name: kPublicationName,
          k_publication_name_furi: kPublicationNameFuri,
          k_industry_code: kIndustryCode,
          k_stock_status: kStockStatus,
          k_head_office_pref: kHeadOfficePref,
          k_branch_office_pref: kBranchOfficePref,
          k_employee_number: kEmployeeNumber,
          new_grad_hire_past_3: newGradHirePast3,
          new_grad_leave_past_3: newGradLeavePast3,
          new_grad_hire_gender_past_3: newGradHireGenderPast3,
          average_tenure: averageTenure,
          training_info: trainingInfo,
          self_dev_support: selfDevSupport,
          mentor_system: mentorSystem,
          career_consulting: careerConsulting,
          internal_certification: internalCertification,
          avg_overtime: avgOvertime,
          avg_paid_leave: avgPaidLeave,
          childcare_targets: childcareTargets,
          childcare_taken: childcareTaken,
          women_ratio: womenRatio,
          company_catch: companyCatch,
          business_content: businessContent,
          hq_postal_code: hqPostalCode,
          hq_address: hqAddress,
          hq_phone_number: hqPhoneNumber,
          capital: capital,
          employees_info: employeesInfo,
          sales: sales,
        },
        { withCredentials: true }
      );

      alert('設定を保存しました！');
    } catch (error) {
      console.error("Failed to update basic info:", error);
      alert('設定の保存に失敗しました。');
    }
  };

  // パスワード変更モーダルを開く
  const handleOpenPasswordModal = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordSuccess(false);
    setOpenPasswordModal(true);
  };

  // パスワード変更モーダルを閉じる
  const handleClosePasswordModal = () => {
    setOpenPasswordModal(false);
  };

  // パスワードを更新
  const handleUpdatePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(false);

    // バリデーション
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('すべての項目を入力してください。');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('新しいパスワードが一致しません。');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('パスワードは8文字以上で入力してください。');
      return;
    }

    if (!(/[a-z]/.test(newPassword))) {
      setPasswordError('パスワードには小文字のアルファベットを含める必要があります。');
      return;
    }

    if (!(/[0-9]/.test(newPassword))) {
      setPasswordError('パスワードには数字を含める必要があります。');
      return;
    }

    try {
      await axios.patch(
        `${BACKEND_URL}/users/me/password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        { withCredentials: true }
      );
      setPasswordSuccess(true);
      setTimeout(() => {
        handleClosePasswordModal();
      }, 2000);
    } catch (err) {
      console.error('パスワード更新失敗:', err);
      setPasswordError(err.response?.data?.detail || 'パスワードの更新に失敗しました。');
    }
  };

  // 子アカウント管理関数
  const fetchSubAccounts = useCallback(async () => {
    setSubAccountLoading(true);
    setSubAccountError(null);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/users/sub-accounts`, {
        withCredentials: true,
      });
      setSubAccounts(response.data);
    } catch (error) {
      setSubAccountError(error.response?.data?.detail || '子アカウントの取得に失敗しました');
    } finally {
      setSubAccountLoading(false);
    }
  }, [BACKEND_URL]);

  const handleCreateSubAccount = async () => {
    if (!newSubAccount.email || !newSubAccount.password) {
      setSnackbarMessage('メールアドレスとパスワードを入力してください');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/users/sub-accounts`, {
        ...newSubAccount,
        role: 'interview_only'
      }, {
        withCredentials: true,
      });
      
      setSnackbarMessage('子アカウントを作成しました');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenSubAccountModal(false);
      setNewSubAccount({ email: '', password: '' });
      fetchSubAccounts();
    } catch (error) {
      setSnackbarMessage(error.response?.data?.detail || '子アカウントの作成に失敗しました');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteSubAccount = async (subAccountId) => {
    if (!window.confirm('このアカウントを削除しますか？')) return;

    try {
      await axios.delete(`${BACKEND_URL}/users/sub-accounts/${subAccountId}`, {
        withCredentials: true,
      });
      
      setSnackbarMessage('子アカウントを削除しました');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchSubAccounts();
    } catch (error) {
      setSnackbarMessage(error.response?.data?.detail || '子アカウントの削除に失敗しました');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleToggleSubAccountLock = async (subAccount) => {
    try {
      await axios.put(`${BACKEND_URL}/users/sub-accounts/${subAccount.id}`, {
        locked: !subAccount.locked
      }, {
        withCredentials: true,
      });
      
      setSnackbarMessage(`アカウントを${subAccount.locked ? 'ロック解除' : 'ロック'}しました`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchSubAccounts();
    } catch (error) {
      setSnackbarMessage(error.response?.data?.detail || 'アカウントの状態変更に失敗しました');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // 子アカウント管理の初期読み込み
  useEffect(() => {
    if (currentUser?.role && ['admin', 'general'].includes(currentUser.role)) {
      fetchSubAccounts();
    }
  }, [currentUser, fetchSubAccounts]);

  // interview_onlyロールのユーザーは設定画面に基本的にアクセスできないが、万が一の場合の制御
  if (currentUser?.role === 'interview_only') {
    return (
      <AppLayout>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <Alert severity="warning" sx={{ maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              アクセス権限がありません
            </Typography>
            <Typography>
              このアカウントは面接サポート機能のみ利用可能です。設定画面へのアクセスはできません。
            </Typography>
          </Alert>
        </Box>
      </AppLayout>
    );
  }

  // ==============================
  // レンダリング
  // ==============================
  return (
    <AppLayout>
          {/* ページヘッダー */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              システム設定
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleOpenPasswordModal}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                }}
              >
                パスワード変更
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSave}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: 3,
                }}
              >
                変更を保存
              </Button>
            </Box>
          </Box>

          {/* 表示設定 */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              表示設定
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={mode === 'dark'}
                  onChange={toggleTheme}
                  icon={<LightModeIcon sx={{ fontSize: 20 }} />}
                  checkedIcon={<DarkModeIcon sx={{ fontSize: 20 }} />}
                />
              }
              label={mode === 'dark' ? 'ダークモード' : 'ライトモード'}
            />
          </Paper>

          {/* アコーディオンコンテナ */}
          <Box sx={{ 
            '& .MuiAccordion-root': {
              mb: 2,
              borderRadius: '8px',
              '&:before': {
                display: 'none',
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            },
            '& .MuiAccordionSummary-root': {
              borderRadius: '8px',
              backgroundColor: theme.palette.background.paper,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            },
            '& .MuiAccordionSummary-content': {
              my: 1.5,
            },
            '& .MuiAccordionDetails-root': {
              p: 3,
              backgroundColor: theme.palette.background.paper,
            },
          }}>
            {/* 1) 基本情報 & 顧客情報・検索キー */}
            <Accordion
              expanded={expanded === 'panel1'}
              onChange={handleChange('panel1')}
              TransitionProps={{ timeout: 0 }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    display: 'flex',
                    alignItems: 'center',
                  }
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  基本情報 & 顧客情報・検索キー
                </Typography>
                {expanded === 'panel1' && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    会社の基本情報と募集媒体掲載用の情報を設定
                  </Typography>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                    基本情報
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="会社名"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        helperText="正式な会社名を入力してください"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="従業員規模"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={scale}
                        onChange={(e) => setScale(e.target.value)}
                        helperText="例：50名、100〜200名など"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="所在地"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        helperText="例：東京都千代田区"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="業種"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        helperText="例：IT、製造、小売など"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                    募集媒体掲載情報
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    募集媒体に掲載する際に必要となる情報です。正確に入力してください。
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="掲載社名"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={kPublicationName}
                        onChange={(e) => setKPublicationName(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="掲載社名(フリガナ)"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={kPublicationNameFuri}
                        onChange={(e) => setKPublicationNameFuri(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl 
                        fullWidth 
                        size="small" 
                        required
                        variant="outlined"
                      >
                        <InputLabel>業種コード</InputLabel>
                        <Select
                          label="業種コード"
                          value={kIndustryCode}
                          onChange={(e) => setKIndustryCode(e.target.value)}
                        >
                          <MenuItem value="">選択してください</MenuItem>
                          <MenuItem value="01">01 - 農業</MenuItem>
                          <MenuItem value="02">02 - 林業</MenuItem>
                          <MenuItem value="03">03 - 漁業（水産養殖業を除く）</MenuItem>
                          <MenuItem value="04">04 - 水産養殖業</MenuItem>
                          <MenuItem value="05">05 - 鉱業，採石業，砂利採取業</MenuItem>
                          <MenuItem value="06">06 - 総合工事業</MenuItem>
                          <MenuItem value="07">07 - 職別工事業(設備工事業を除く)</MenuItem>
                          <MenuItem value="08">08 - 設備工事業</MenuItem>
                          <MenuItem value="09">09 - 食料品製造業</MenuItem>
                          <MenuItem value="10">10 - 飲料・たばこ・飼料製造業</MenuItem>
                          <MenuItem value="11">11 - 繊維工業</MenuItem>
                          <MenuItem value="12">12 - 木材・木製品製造業（家具を除く）</MenuItem>
                          <MenuItem value="13">13 - 家具・装備品製造業</MenuItem>
                          <MenuItem value="14">14 - パルプ・紙・紙加工品製造業</MenuItem>
                          <MenuItem value="15">15 - 印刷・同関連業</MenuItem>
                          <MenuItem value="16">16 - 化学工業</MenuItem>
                          <MenuItem value="17">17 - 石油製品・石炭製品製造業</MenuItem>
                          <MenuItem value="18">18 - プラスチック製品製造業</MenuItem>
                          <MenuItem value="19">19 - ゴム製品製造業</MenuItem>
                          <MenuItem value="20">20 - なめし革・同製品・毛皮製造業</MenuItem>
                          <MenuItem value="21">21 - 窯業・土石製品製造業</MenuItem>
                          <MenuItem value="22">22 - 鉄鋼業</MenuItem>
                          <MenuItem value="23">23 - 非鉄金属製造業</MenuItem>
                          <MenuItem value="24">24 - 金属製品製造業</MenuItem>
                          <MenuItem value="25">25 - はん用機械器具製造業</MenuItem>
                          <MenuItem value="26">26 - 生産用機械器具製造業</MenuItem>
                          <MenuItem value="27">27 - 業務用機械器具製造業</MenuItem>
                          <MenuItem value="28">28 - 電子部品・デバイス・電子回路製造業</MenuItem>
                          <MenuItem value="29">29 - 電気機械器具製造業</MenuItem>
                          <MenuItem value="30">30 - 情報通信機械器具製造業</MenuItem>
                          <MenuItem value="31">31 - 輸送用機械器具製造業</MenuItem>
                          <MenuItem value="32">32 - その他の製造業</MenuItem>
                          <MenuItem value="33">33 - 電気業</MenuItem>
                          <MenuItem value="34">34 - ガス業</MenuItem>
                          <MenuItem value="35">35 - 熱供給業</MenuItem>
                          <MenuItem value="36">36 - 水道業</MenuItem>
                          <MenuItem value="37">37 - 通信業</MenuItem>
                          <MenuItem value="38">38 - 放送業</MenuItem>
                          <MenuItem value="39">39 - 情報サービス業</MenuItem>
                          <MenuItem value="40">40 - インターネット附随サービス業</MenuItem>
                          <MenuItem value="41">41 - 映像・音声・文字情報制作業</MenuItem>
                          <MenuItem value="42">42 - 鉄道業</MenuItem>
                          <MenuItem value="43">43 - 道路旅客運送業</MenuItem>
                          <MenuItem value="44">44 - 道路貨物運送業</MenuItem>
                          <MenuItem value="45">45 - 水運業</MenuItem>
                          <MenuItem value="46">46 - 航空運輸業</MenuItem>
                          <MenuItem value="47">47 - 倉庫業</MenuItem>
                          <MenuItem value="48">48 - 運輸に附帯するサービス業</MenuItem>
                          <MenuItem value="49">49 - 郵便業（信書便事業を含む）</MenuItem>
                          <MenuItem value="50">50 - 各種商品卸売業</MenuItem>
                          <MenuItem value="51">51 - 繊維・衣服等卸売業</MenuItem>
                          <MenuItem value="52">52 - 飲食料品卸売業</MenuItem>
                          <MenuItem value="53">53 - 建築材料，鉱物・金属材料等卸売業</MenuItem>
                          <MenuItem value="54">54 - 機械器具卸売業</MenuItem>
                          <MenuItem value="55">55 - その他の卸売業</MenuItem>
                          <MenuItem value="56">56 - 各種商品小売業</MenuItem>
                          <MenuItem value="57">57 - 織物・衣服・身の回り品小売業</MenuItem>
                          <MenuItem value="58">58 - 飲食料品小売業</MenuItem>
                          <MenuItem value="59">59 - 機械器具小売業</MenuItem>
                          <MenuItem value="60">60 - その他の小売業</MenuItem>
                          <MenuItem value="61">61 - 無店舗小売業</MenuItem>
                          <MenuItem value="62">62 - 銀行業</MenuItem>
                          <MenuItem value="63">63 - 協同組織金融業</MenuItem>
                          <MenuItem value="64">64 - 貸金業，クレジットカード業等非預金信用機関</MenuItem>
                          <MenuItem value="65">65 - 金融商品取引業，商品先物取引業</MenuItem>
                          <MenuItem value="66">66 - 補助的金融業等</MenuItem>
                          <MenuItem value="67">67 - 保険業（保険媒介代理業，保険サービス業を含む）</MenuItem>
                          <MenuItem value="68">68 - 不動産取引業</MenuItem>
                          <MenuItem value="69">69 - 不動産賃貸業・管理業</MenuItem>
                          <MenuItem value="70">70 - 物品賃貸業</MenuItem>
                          <MenuItem value="71">71 - 学術・開発研究機関</MenuItem>
                          <MenuItem value="72">72 - 専門サービス業（他に分類されないもの）</MenuItem>
                          <MenuItem value="73">73 - 広告業</MenuItem>
                          <MenuItem value="74">74 - 技術サービス業（他に分類されないもの）</MenuItem>
                          <MenuItem value="75">75 - 宿泊業</MenuItem>
                          <MenuItem value="76">76 - 飲食店</MenuItem>
                          <MenuItem value="77">77 - 持ち帰り・配達飲食サービス業</MenuItem>
                          <MenuItem value="78">78 - 洗濯・理容・美容・浴場業</MenuItem>
                          <MenuItem value="79">79 - その他の生活関連サービス業</MenuItem>
                          <MenuItem value="80">80 - 娯楽業</MenuItem>
                          <MenuItem value="81">81 - 学校教育</MenuItem>
                          <MenuItem value="82">82 - その他の教育，学習支援業</MenuItem>
                          <MenuItem value="83">83 - 医療業</MenuItem>
                          <MenuItem value="84">84 - 保健衛生</MenuItem>
                          <MenuItem value="85">85 - 社会保険・社会福祉・介護事業</MenuItem>
                          <MenuItem value="86">86 - 郵便局</MenuItem>
                          <MenuItem value="87">87 - 協同組合（他に分類されないもの）</MenuItem>
                          <MenuItem value="88">88 - 廃棄物処理業</MenuItem>
                          <MenuItem value="89">89 - 自動車整備業</MenuItem>
                          <MenuItem value="90">90 - 機械等修理業</MenuItem>
                          <MenuItem value="91">91 - 職業紹介・労働者派遣業</MenuItem>
                          <MenuItem value="92">92 - その他の事業サービス業</MenuItem>
                          <MenuItem value="93">93 - 政治・経済・文化団体</MenuItem>
                          <MenuItem value="94">94 - 宗教</MenuItem>
                          <MenuItem value="95">95 - その他のサービス業</MenuItem>
                          <MenuItem value="96">96 - 外国公務</MenuItem>
                          <MenuItem value="97">97 - 国家公務</MenuItem>
                          <MenuItem value="98">98 - 地方公務</MenuItem>
                          <MenuItem value="99">99 - 分類不能の産業</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl 
                        fullWidth 
                        size="small" 
                        required
                        variant="outlined"
                      >
                        <InputLabel>株式公開状況</InputLabel>
                        <Select
                          label="株式公開状況"
                          value={kStockStatus}
                          onChange={(e) => setKStockStatus(e.target.value)}
                        >
                          <MenuItem value="未公開">未公開</MenuItem>
                          <MenuItem value="公開">公開</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="本社所在地(都道府県)"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={kHeadOfficePref}
                        onChange={(e) => setKHeadOfficePref(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="事業所所在地(都道府県)"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={kBranchOfficePref}
                        onChange={(e) => setKBranchOfficePref(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="従業員数"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={kEmployeeNumber}
                        onChange={(e) => handleNumericInput(e, setKEmployeeNumber)}
                        placeholder="例: 100"
                        helperText={kEmployeeNumber ? formatEmployee(kEmployeeNumber) : '数値のみ入力してください（例: 100）'}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* 2) 雇用促進法明示項目 */}
            <Accordion
              expanded={expanded === 'panel2'}
              onChange={handleChange('panel2')}
              TransitionProps={{ timeout: 0 }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    display: 'flex',
                    alignItems: 'center',
                  }
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  雇用促進法明示項目
                </Typography>
                {expanded === 'panel2' && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    法定開示情報の設定
                  </Typography>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  これらの項目は青少年の雇用の促進等に関する法律に基づく開示項目です。正確な情報を入力してください。
                </Alert>

                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                    A) 募集・採用に関する状況
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="過去３年間の新卒採用者数"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={newGradHirePast3}
                        onChange={(e) => setNewGradHirePast3(e.target.value)}
                        helperText="例：20名（2021年度：8名、2022年度：6名、2023年度：6名））"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="過去３年間の新卒離職者数"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={newGradLeavePast3}
                        onChange={(e) => setNewGradLeavePast3(e.target.value)}
                        helperText="例：10名（2021年度：5名、2022年度：3名、2023年度：2名）"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="過去３年間の新卒採用者数（男女別）"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={newGradHireGenderPast3}
                        onChange={(e) => setNewGradHireGenderPast3(e.target.value)}
                        helperText="例：男性10名、女性10名"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="平均勤続年数"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={averageTenure}
                        onChange={(e) => setAverageTenure(e.target.value)}
                        helperText="例：3.5年"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                    B) 職業能力開発
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="研修の有無及び内容"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={trainingInfo}
                        onChange={(e) => setTrainingInfo(e.target.value)}
                        helperText="例：新入社員研修、キャリアアップ研修など"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="自己啓発支援の有無及び内容"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={selfDevSupport}
                        onChange={(e) => setSelfDevSupport(e.target.value)}
                        helperText="例：自己啓発セミナー参加、自己啓発書籍購入など"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="メンター制度の有無"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={mentorSystem}
                        onChange={(e) => setMentorSystem(e.target.value)}
                        helperText="例：メンター制度の実施状況"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="キャリアコンサルティング制度の有無及び内容"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={careerConsulting}
                        onChange={(e) => setCareerConsulting(e.target.value)}
                        helperText="例：キャリアコンサルティングの実施状況"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="社内検定等の制度の有無及び内容"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={internalCertification}
                        onChange={(e) => setInternalCertification(e.target.value)}
                        helperText="例：社内検定の実施状況"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                    C) 雇用管理
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="前年度の月平均所定外労働時間の実績"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={avgOvertime}
                        onChange={(e) => setAvgOvertime(e.target.value)}
                        helperText="例：10時間"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="前年度の有給休暇の平均取得"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={avgPaidLeave}
                        onChange={(e) => setAvgPaidLeave(e.target.value)}
                        helperText="例：10日"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="前年度の育児休業取得対象者数（男女別）"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={childcareTargets}
                        onChange={(e) => setChildcareTargets(e.target.value)}
                        helperText="例：男性5名、女性5名"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="前年度の育児休業取得者数（男女別）"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={childcareTaken}
                        onChange={(e) => setChildcareTaken(e.target.value)}
                        helperText="例：男性4名、女性4名"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="役員/管理職に占める女性の割合"
                        required
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={womenRatio}
                        onChange={(e) => setWomenRatio(e.target.value)}
                        helperText="例：30%"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* 3) 企業基本情報 */}
            <Accordion
              expanded={expanded === 'panel3'}
              onChange={handleChange('panel3')}
              TransitionProps={{ timeout: 0 }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    display: 'flex',
                    alignItems: 'center',
                  }
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  企業基本情報
                </Typography>
                {expanded === 'panel3' && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    企業プロフィールの詳細情報
                  </Typography>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="企業キャッチコピー"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={companyCatch}
                      onChange={(e) => setCompanyCatch(e.target.value)}
                      helperText="企業の特徴を簡潔に表現してください"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="事業内容"
                      variant="outlined"
                      fullWidth
                      size="small"
                      multiline
                      minRows={3}
                      value={businessContent}
                      onChange={(e) => setBusinessContent(e.target.value)}
                      helperText="主な事業内容を具体的に記載してください"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="本社郵便番号"
                      required
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={hqPostalCode}
                      onChange={(e) => setHqPostalCode(e.target.value)}
                      helperText="例：100-0001"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="本社所在地"
                      required
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={hqAddress}
                      onChange={(e) => setHqAddress(e.target.value)}
                      helperText="例：東京都千代田区"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="本社電話番号"
                      required
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={hqPhoneNumber}
                      onChange={(e) => setHqPhoneNumber(e.target.value)}
                      helperText="例：03-1234-5678"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* 4) 会社概要 */}
            <Accordion
              expanded={expanded === 'panel4'}
              onChange={handleChange('panel4')}
              TransitionProps={{ timeout: 0 }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    display: 'flex',
                    alignItems: 'center',
                  }
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  会社概要
                </Typography>
                {expanded === 'panel4' && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    会社の規模や実績に関する情報
                  </Typography>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="資本金"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={capital}
                      onChange={(e) => handleNumericInput(e, setCapital)}
                      placeholder="例: 100000000"
                      helperText={capital ? formatCurrency(capital) : '※ 半角数字のみ入力してください（例: 100000000）'}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="従業員数"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={employeesInfo}
                      onChange={(e) => setEmployeesInfo(e.target.value)}
                      helperText="例：正社員100名（2024年4月現在）"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="売上高"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={sales}
                      onChange={(e) => handleNumericInput(e, setSales)}
                      placeholder="例: 5000000000"
                      helperText={sales ? formatCurrency(sales) : '※ 半角数字のみ入力してください（例: 5000000000）'}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* 5) 子アカウント管理（generalとadminロールのみ） */}
            {currentUser?.role && ['admin', 'general'].includes(currentUser.role) && (
              <Accordion
                expanded={expanded === 'panel5'}
                onChange={handleChange('panel5')}
                TransitionProps={{ timeout: 0 }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      display: 'flex',
                      alignItems: 'center',
                    }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    子アカウント管理
                  </Typography>
                  {expanded === 'panel5' && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      面接サポート専用アカウントの管理
                    </Typography>
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" color="primary">
                      子アカウント一覧
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<PersonAddIcon />}
                      onClick={() => setOpenSubAccountModal(true)}
                      sx={{ borderRadius: '20px' }}
                    >
                      新規作成
                    </Button>
                  </Box>

                  {subAccountError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {subAccountError}
                    </Alert>
                  )}

                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>メールアドレス</TableCell>
                          <TableCell>権限</TableCell>
                          <TableCell>状態</TableCell>
                          <TableCell>作成日</TableCell>
                          <TableCell align="center">操作</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subAccountLoading ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              読み込み中...
                            </TableCell>
                          </TableRow>
                        ) : subAccounts.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              子アカウントがありません
                            </TableCell>
                          </TableRow>
                        ) : (
                          subAccounts.map((subAccount) => (
                            <TableRow key={subAccount.id}>
                              <TableCell>{subAccount.email}</TableCell>
                              <TableCell>
                                <Chip 
                                  label="面接専用" 
                                  size="small" 
                                  color="primary" 
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={subAccount.locked ? "ロック中" : "アクティブ"} 
                                  size="small" 
                                  color={subAccount.locked ? "error" : "success"}
                                />
                              </TableCell>
                              <TableCell>
                                {new Date(subAccount.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title={subAccount.locked ? "ロック解除" : "ロック"}>
                                  <IconButton
                                    onClick={() => handleToggleSubAccountLock(subAccount)}
                                    color={subAccount.locked ? "success" : "warning"}
                                  >
                                    {subAccount.locked ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="削除">
                                  <IconButton
                                    onClick={() => handleDeleteSubAccount(subAccount.id)}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    子アカウントは面接サポート機能のみ利用可能です。子アカウントユーザーはこのシステムの他の機能にはアクセスできません。
                  </Alert>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>

          {/* フローティング保存ボタン */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
              display: { xs: 'block', sm: 'none' }
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSave}
              sx={{
                borderRadius: '50%',
                width: 64,
                height: 64,
                minWidth: 'unset',
                boxShadow: 4,
              }}
            >
              <SaveIcon />
            </Button>
          </Box>

          {/* パスワード変更モーダル */}
          <Dialog open={openPasswordModal} onClose={handleClosePasswordModal}>
            <DialogTitle>パスワード変更</DialogTitle>
            <DialogContent>
              {passwordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {passwordError}
                </Alert>
              )}
              {passwordSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  パスワードを更新しました。
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: '400px' }}>
                <TextField
                  label="現在のパスワード"
                  type="password"
                  fullWidth
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <TextField
                  label="新しいパスワード"
                  type="password"
                  fullWidth
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  helperText="8文字以上で、小文字のアルファベットと数字を含める必要があります"
                />
                <TextField
                  label="新しいパスワード（確認）"
                  type="password"
                  fullWidth
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClosePasswordModal}>キャンセル</Button>
              <Button
                variant="contained"
                onClick={handleUpdatePassword}
                disabled={!currentPassword || !newPassword || !confirmPassword || passwordSuccess}
              >
                変更
              </Button>
            </DialogActions>
          </Dialog>

          {/* 子アカウント作成モーダル */}
          <Dialog 
            open={openSubAccountModal} 
            onClose={() => setOpenSubAccountModal(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>新しい子アカウントを作成</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: '400px' }}>
                <TextField
                  label="メールアドレス"
                  type="email"
                  fullWidth
                  value={newSubAccount.email}
                  onChange={(e) => setNewSubAccount({ ...newSubAccount, email: e.target.value })}
                  required
                />
                <TextField
                  label="パスワード"
                  type={showSubAccountPassword ? 'text' : 'password'}
                  fullWidth
                  value={newSubAccount.password}
                  onChange={(e) => setNewSubAccount({ ...newSubAccount, password: e.target.value })}
                  required
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowSubAccountPassword(!showSubAccountPassword)}
                        edge="end"
                      >
                        {showSubAccountPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    )
                  }}
                />
                <Alert severity="info">
                  作成される子アカウントは面接サポート機能のみ利用可能です。
                </Alert>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenSubAccountModal(false)}>
                キャンセル
              </Button>
              <Button 
                onClick={handleCreateSubAccount} 
                variant="contained"
                disabled={!newSubAccount.email || !newSubAccount.password}
              >
                作成
              </Button>
            </DialogActions>
          </Dialog>

          {/* スナックバー */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
          >
            <Alert 
              onClose={() => setSnackbarOpen(false)} 
              severity={snackbarSeverity}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
    </AppLayout>
  );
}

export default Settings;